import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getToken } from '../auth';
import {
  Card,
  Button,
  Tag,
  Progress,
  Typography,
  Space,
  Spin,
  Statistic,
  Row,
  Col,
  Descriptions,
  message,
  Table,
  Alert,
  Popconfirm,
  Modal,
  Select,
  Input,
  Radio,
} from 'antd';
import {
  ArrowLeftOutlined,
  EyeOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  ForwardOutlined,
  DownloadOutlined,
  FolderAddOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import ModerationViewer from './ModerationViewer';

const { Title, Text } = Typography;

interface ModerationSession {
  id: number;
  name: string;
  dataset_id: number;
  dataset_name: string;
  dataset_type: string;
  status: string;
  filter_params: any;
  total_images: number;
  current_index: number;
  approved_count: number;
  declined_count: number;
  skipped_count: number;
  elapsed_seconds: number;
  last_resumed_at: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  source_type: string;
  source_session_id: number | null;
  source_session_name: string | null;
  last_synced_at: string | null;
}

interface Decision {
  id: number;
  session_id: number;
  image_id: number;
  decision: string;
  decided_at: string;
  s3_url: string;
  thumbnail_uri: string;
}

interface WatermarkProgress {
  status: string; // running | completed | failed
  processed: number;
  total: number;
  approved: number;
  removed: number;
  phase?: string;
  error?: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${mins}m`;
};

const ModerationSessionView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<ModerationSession | null>(null);
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // "Convert to Collection" modal state
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertSource, setConvertSource] = useState<'approved' | 'declined' | 'favorites'>('approved');
  const [collections, setCollections] = useState<{ id: number; name: string; image_count: number }[]>([]);
  const [convertTarget, setConvertTarget] = useState<number | 'new'>('new');
  const [convertName, setConvertName] = useState('');
  const [converting, setConverting] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const liveSyncRef = useRef<NodeJS.Timeout | null>(null);
  const autoSyncedRef = useRef(false);
  // A live re-sync recomputes the whole queue and can take tens of seconds on
  // large sessions; this guard prevents overlapping syncs from stacking up.
  const syncInFlightRef = useRef(false);

  // Both dataset- and session-sourced sessions track a live source, so newly-added
  // (dataset) or newly-approved (parent) images should keep flowing into the queue.
  const isLive = session?.source_type === 'session' || session?.source_type === 'dataset';

  useEffect(() => {
    autoSyncedRef.current = false;
    fetchSession();
    fetchDecisions();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [id]);

  // Auto-sync a live task once when it loads, so newly-added (dataset) or
  // newly-approved (parent) images appear without a manual refresh.
  useEffect(() => {
    if (isLive && !autoSyncedRef.current) {
      autoSyncedRef.current = true;
      handleSync(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id, session?.source_type]);

  // While the viewer is open on a live, active session, keep re-syncing so images
  // another moderator adds to the source keep arriving. handleSync() refreshes the
  // session, so the updated total_images flows down to the viewer as a prop.
  useEffect(() => {
    if (liveSyncRef.current) clearInterval(liveSyncRef.current);
    if (viewerOpen && isLive && session?.status === 'active') {
      liveSyncRef.current = setInterval(() => handleSync(true), 30000);
    }
    return () => {
      if (liveSyncRef.current) clearInterval(liveSyncRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerOpen, isLive, session?.status]);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (session?.status === 'active') {
      intervalRef.current = setInterval(() => {
        fetchSession();
        fetchDecisions();
      }, 3000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [session?.status]);

  const fetchSession = async () => {
    try {
      const response = await axios.get(`${API_URL}/moderation-sessions/${id}`);
      setSession(response.data);
    } catch (error) {
      message.error('Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  const fetchDecisions = async () => {
    try {
      const response = await axios.get(`${API_URL}/moderation-sessions/${id}/decisions`);
      setDecisions(response.data);
    } catch (error) {
      console.error('Failed to fetch decisions:', error);
    }
  };

  // Open the viewer immediately, then refresh the live queue in the background.
  // A live sync can take tens of seconds on large sessions, so awaiting it here
  // would make "Open Viewer" appear to hang / do nothing.
  const openViewer = () => {
    setViewerOpen(true);
    if (isLive) handleSync(true);
  };

  const handleStart = async () => {
    try {
      await axios.post(`${API_URL}/moderation-sessions/${id}/start`);
      await fetchSession();
      await openViewer();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to start session');
    }
  };

  const handleResume = async () => {
    try {
      await axios.post(`${API_URL}/moderation-sessions/${id}/resume`);
      await fetchSession();
      await openViewer();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to resume session');
    }
  };

  const handlePause = async () => {
    try {
      await axios.post(`${API_URL}/moderation-sessions/${id}/pause`);
      fetchSession();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to pause session');
    }
  };

  const handleComplete = async () => {
    try {
      await axios.post(`${API_URL}/moderation-sessions/${id}/complete`);
      fetchSession();
      fetchDecisions();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to complete session');
    }
  };

  const handleSync = async (silent = false) => {
    // Skip if a sync is already running — these are expensive and must not stack.
    if (syncInFlightRef.current) return;
    syncInFlightRef.current = true;
    setSyncing(true);
    try {
      const res = await axios.post(`${API_URL}/moderation-sessions/${id}/sync`);
      await fetchSession();
      await fetchDecisions();
      if (!silent) {
        message.success(`Synced from source: +${res.data.added} new, −${res.data.removed} removed`);
      }
    } catch (error: any) {
      if (!silent) message.error(error.response?.data?.error || 'Failed to sync from source');
    } finally {
      setSyncing(false);
      syncInFlightRef.current = false;
    }
  };

  // Background watermark-approval job + realtime progress over WebSocket.
  const [wmProgress, setWmProgress] = useState<WatermarkProgress | null>(null);
  const wmWsRef = useRef<WebSocket | null>(null);

  const subscribeWatermark = useCallback(() => {
    if (wmWsRef.current) return;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${protocol}//${process.env.REACT_APP_WS_HOST || 'localhost:3003'}/ws`);
    wmWsRef.current = ws;
    ws.onopen = () => ws.send(JSON.stringify({ action: 'subscribe', channel: `moderation-watermark:${id}` }));
    ws.onmessage = (event) => {
      try {
        const { data } = JSON.parse(event.data);
        if (data?.type !== 'watermark-approve-progress') return;
        setWmProgress(data);
        if (data.status === 'completed' || data.status === 'failed') {
          ws.close();
          wmWsRef.current = null;
          if (data.status === 'completed') {
            message.success(`Approved ${data.approved} photo(s); ${data.removed} removed from the queue`);
          } else {
            message.error(`Approve by watermark failed: ${data.error || 'unknown error'}`);
          }
          fetchSession();
          fetchDecisions();
          setTimeout(() => setWmProgress(null), 1500);
        }
      } catch (_) { /* ignore parse errors */ }
    };
    ws.onclose = () => { wmWsRef.current = null; };
  }, [id]);

  const handleApproveByWatermark = async () => {
    try {
      setWmProgress({ status: 'running', processed: 0, total: 0, approved: 0, removed: 0 });
      subscribeWatermark();
      await axios.post(`${API_URL}/moderation-sessions/${id}/approve-by-watermark`);
    } catch (error: any) {
      setWmProgress(null);
      if (wmWsRef.current) { wmWsRef.current.close(); wmWsRef.current = null; }
      message.error(error.response?.data?.error || 'Failed to start approve by watermark');
    }
  };

  // Re-attach to an in-flight job after a page reload
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/moderation-sessions/${id}/approve-by-watermark/status`);
        if (!cancelled && res.data.running) {
          setWmProgress(res.data.progress || { status: 'running', processed: 0, total: 0, approved: 0, removed: 0 });
          subscribeWatermark();
        }
      } catch (_) { /* no job running */ }
    })();
    return () => {
      cancelled = true;
      if (wmWsRef.current) { wmWsRef.current.close(); wmWsRef.current = null; }
    };
  }, [id, subscribeWatermark]);

  // ----- Approve-by-gallery (Pornpics): same cascade as watermark, keyed on
  // gallery_source_id. Same UX as the watermark button.
  const [galleryProgress, setGalleryProgress] = useState<WatermarkProgress | null>(null);
  const galleryWsRef = useRef<WebSocket | null>(null);

  const subscribeGallery = useCallback(() => {
    if (galleryWsRef.current) return;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${protocol}//${process.env.REACT_APP_WS_HOST || 'localhost:3003'}/ws`);
    galleryWsRef.current = ws;
    ws.onopen = () => ws.send(JSON.stringify({ action: 'subscribe', channel: `moderation-gallery:${id}` }));
    ws.onmessage = (event) => {
      try {
        const { data } = JSON.parse(event.data);
        if (data?.type !== 'gallery-approve-progress') return;
        setGalleryProgress(data);
        if (data.status === 'completed' || data.status === 'failed') {
          ws.close();
          galleryWsRef.current = null;
          if (data.status === 'completed') {
            message.success(`Approved ${data.approved} photo(s); ${data.removed} removed from the queue`);
          } else {
            message.error(`Approve by gallery failed: ${data.error || 'unknown error'}`);
          }
          fetchSession();
          fetchDecisions();
          setTimeout(() => setGalleryProgress(null), 1500);
        }
      } catch (_) { /* ignore parse errors */ }
    };
    ws.onclose = () => { galleryWsRef.current = null; };
  }, [id]);

  const handleApproveByGallery = async () => {
    try {
      setGalleryProgress({ status: 'running', processed: 0, total: 0, approved: 0, removed: 0 });
      subscribeGallery();
      await axios.post(`${API_URL}/moderation-sessions/${id}/approve-by-gallery`);
    } catch (error: any) {
      setGalleryProgress(null);
      if (galleryWsRef.current) { galleryWsRef.current.close(); galleryWsRef.current = null; }
      message.error(error.response?.data?.error || 'Failed to start approve by gallery');
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/moderation-sessions/${id}/approve-by-gallery/status`);
        if (!cancelled && res.data.running) {
          setGalleryProgress(res.data.progress || { status: 'running', processed: 0, total: 0, approved: 0, removed: 0 });
          subscribeGallery();
        }
      } catch (_) { /* no job running */ }
    })();
    return () => {
      cancelled = true;
      if (galleryWsRef.current) { galleryWsRef.current.close(); galleryWsRef.current = null; }
    };
  }, [id, subscribeGallery]);

  const handleExportApproved = () => {
    // Browser-native download can't send the auth header, so pass the token as a
    // query param (the /api guard accepts ?token= as a fallback).
    const token = encodeURIComponent(getToken() || '');
    window.open(`${API_URL}/moderation-sessions/${id}/export-approved?token=${token}`, '_blank');
  };

  const openConvert = async () => {
    setConvertOpen(true);
    try {
      const res = await axios.get(`${API_URL}/collections`);
      setCollections(res.data);
    } catch {
      setCollections([]);
    }
  };

  const handleConvert = async () => {
    if (convertTarget === 'new' && !convertName.trim()) {
      message.error('Enter a name for the new collection');
      return;
    }
    setConverting(true);
    try {
      const body: any = { source: convertSource };
      if (convertTarget === 'new') body.name = convertName.trim();
      else body.collection_id = convertTarget;
      const res = await axios.post(`${API_URL}/moderation-sessions/${id}/to-collection`, body);
      const { collection, added, total } = res.data;
      message.success(`Added ${added} image(s) to "${collection.name}" (${total} total)`);
      setConvertOpen(false);
      setConvertName('');
    } catch (err: any) {
      message.error(err?.response?.data?.error || 'Failed to convert to collection');
    } finally {
      setConverting(false);
    }
  };

  const handleViewerClose = () => {
    setViewerOpen(false);
    fetchSession();
    fetchDecisions();
  };

  const getStatusTag = (status: string) => {
    const config: Record<string, { color: string; icon: React.ReactNode }> = {
      pending: { color: 'default', icon: <ClockCircleOutlined /> },
      active: { color: 'processing', icon: <SyncOutlined spin /> },
      paused: { color: 'warning', icon: <PauseCircleOutlined /> },
      completed: { color: 'success', icon: <CheckCircleOutlined /> },
    };
    const c = config[status] || { color: 'default', icon: null };
    return <Tag color={c.color} icon={c.icon} style={{ fontSize: 14, padding: '4px 12px' }}>{status.toUpperCase()}</Tag>;
  };

  if (loading) {
    return <div style={{ padding: 24, textAlign: 'center' }}><Spin size="large" /></div>;
  }

  if (!session) {
    return <div style={{ padding: 24 }}><Alert message="Session not found" type="error" /></div>;
  }

  const reviewed = session.approved_count + session.declined_count + session.skipped_count;
  const remaining = session.total_images - reviewed;
  const progressPercent = session.total_images > 0 ? Math.round((reviewed / session.total_images) * 100) : 0;

  const decisionColumns = [
    {
      title: 'Image ID',
      dataIndex: 'image_id',
      key: 'image_id',
      width: 100,
    },
    {
      title: 'Decision',
      dataIndex: 'decision',
      key: 'decision',
      width: 120,
      render: (decision: string) => {
        const colors: Record<string, string> = { approved: 'success', declined: 'error', skipped: 'default' };
        return <Tag color={colors[decision] || 'default'}>{decision.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Time',
      dataIndex: 'decided_at',
      key: 'decided_at',
      render: (ts: string) => new Date(ts).toLocaleString(),
    },
  ];

  // Format filter params for display
  const filterSummary = () => {
    const fp = session.filter_params;
    if (!fp || Object.keys(fp).length === 0) return 'No filters applied';
    const parts: string[] = [];
    if (fp.size?.length) parts.push(`Size: ${fp.size.join(', ')}`);
    if (fp.aspectRatio?.length) parts.push(`Aspect Ratio: ${fp.aspectRatio.join(', ')}`);
    if (fp.captionFilters && Object.keys(fp.captionFilters).length > 0) {
      parts.push(`${Object.keys(fp.captionFilters).length} caption filter(s)`);
    }
    return parts.length > 0 ? parts.join(' | ') : 'No filters applied';
  };

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/moderation')} style={{ marginBottom: 16 }}>
            Back to Moderation Sessions
          </Button>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <EyeOutlined style={{ fontSize: 28, color: '#6366f1' }} />
              <Title level={2} style={{ margin: 0 }}>{session.name}</Title>
              {getStatusTag(session.status)}
            </div>
            <Space>
              {session.status === 'pending' && (
                <Button type="primary" icon={<PlayCircleOutlined />} onClick={handleStart} size="large">
                  Start Session
                </Button>
              )}
              {session.status === 'paused' && (
                <Button type="primary" icon={<PlayCircleOutlined />} onClick={handleResume} size="large">
                  Resume
                </Button>
              )}
              {session.status === 'active' && (
                <>
                  <Button icon={<EyeOutlined />} onClick={openViewer} size="large" type="primary">
                    Open Viewer
                  </Button>
                  <Button icon={<PauseCircleOutlined />} onClick={handlePause} size="large">
                    Pause
                  </Button>
                </>
              )}
              {(session.status === 'active' || session.status === 'paused') && (
                <Button icon={<CheckCircleOutlined />} onClick={handleComplete} size="large">
                  Complete
                </Button>
              )}
              {session.source_type === 'session' && (
                <Button icon={<SyncOutlined spin={syncing} />} onClick={() => handleSync(false)}
                  loading={syncing} size="large">
                  Pull new approved
                </Button>
              )}
              {wmProgress ? (
                <div style={{ minWidth: 220 }}>
                  <Text style={{ fontSize: 12 }}>
                    {wmProgress.status === 'completed' ? 'Watermark approval complete'
                      : wmProgress.phase === 'scanning' || wmProgress.total === 0 ? 'Scanning images…'
                      : `Approving ${wmProgress.processed.toLocaleString()} / ${wmProgress.total.toLocaleString()}`}
                  </Text>
                  <Progress
                    percent={wmProgress.status === 'completed' ? 100
                      : wmProgress.total > 0 ? Math.round((wmProgress.processed / wmProgress.total) * 100) : 0}
                    status={wmProgress.status === 'failed' ? 'exception'
                      : wmProgress.status === 'completed' ? 'success' : 'active'}
                    size="small"
                  />
                </div>
              ) : (session.status === 'paused' || session.status === 'completed') && session.approved_count > 0 && (
                <Popconfirm
                  title="Approve photos sharing an approved watermark"
                  description="Watermarks with at least one approved and no declined photo will have their remaining photos approved and removed from the queue. Runs in the background."
                  okText="Approve"
                  onConfirm={handleApproveByWatermark}
                >
                  <Button icon={<CheckCircleOutlined />} size="large">
                    Approve same-watermark photos
                  </Button>
                </Popconfirm>
              )}
              {galleryProgress ? (
                <div style={{ minWidth: 220 }}>
                  <Text style={{ fontSize: 12 }}>
                    {galleryProgress.status === 'completed' ? 'Gallery approval complete'
                      : galleryProgress.phase === 'scanning' || galleryProgress.total === 0 ? 'Scanning images…'
                      : `Approving ${galleryProgress.processed.toLocaleString()} / ${galleryProgress.total.toLocaleString()}`}
                  </Text>
                  <Progress
                    percent={galleryProgress.status === 'completed' ? 100
                      : galleryProgress.total > 0 ? Math.round((galleryProgress.processed / galleryProgress.total) * 100) : 0}
                    status={galleryProgress.status === 'failed' ? 'exception'
                      : galleryProgress.status === 'completed' ? 'success' : 'active'}
                    size="small"
                  />
                </div>
              ) : (session.status === 'paused' || session.status === 'completed') && session.approved_count > 0 && (
                <Popconfirm
                  title="Approve photos sharing an approved gallery"
                  description="Galleries with at least one approved and no declined photo will have their remaining photos approved and removed from the queue. Pornpics-style — runs in the background."
                  okText="Approve"
                  onConfirm={handleApproveByGallery}
                >
                  <Button icon={<CheckCircleOutlined />} size="large">
                    Approve same-gallery photos
                  </Button>
                </Popconfirm>
              )}
              {session.approved_count > 0 && (
                <Button icon={<DownloadOutlined />} onClick={handleExportApproved} size="large">
                  Export Approved ({session.approved_count})
                </Button>
              )}
              {(session.approved_count > 0 || session.declined_count > 0) && (
                <Button icon={<FolderAddOutlined />} onClick={openConvert} size="large">
                  Convert to Collection
                </Button>
              )}
            </Space>
          </div>
        </div>

        {/* Progress */}
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={24} align="middle">
            <Col span={12}>
              <Progress
                percent={progressPercent}
                status={session.status === 'completed' ? 'success' : 'active'}
                strokeWidth={20}
                format={() => `${progressPercent}%`}
              />
              <Text type="secondary">
                {reviewed.toLocaleString()} / {session.total_images.toLocaleString()} reviewed
              </Text>
            </Col>
            <Col span={6}>
              <Statistic title="Time Spent" value={formatDuration(session.elapsed_seconds || 0)}
                prefix={<ClockCircleOutlined />} />
            </Col>
            <Col span={6}>
              <Statistic title="Remaining" value={remaining} prefix={<ForwardOutlined />} />
            </Col>
          </Row>
        </Card>

        {/* Stats */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic title="Total Images" value={session.total_images} valueStyle={{ color: '#1890ff' }} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="Approved" value={session.approved_count} valueStyle={{ color: '#52c41a' }}
                prefix={<CheckCircleOutlined />} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="Declined" value={session.declined_count} valueStyle={{ color: '#ff4d4f' }}
                prefix={<CloseCircleOutlined />} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="Skipped" value={session.skipped_count} valueStyle={{ color: '#faad14' }} />
            </Card>
          </Col>
        </Row>

        {/* Session Details */}
        <Card title="Session Details" style={{ marginBottom: 24 }}>
          <Descriptions column={2}>
            <Descriptions.Item label="Dataset">{session.dataset_name}</Descriptions.Item>
            <Descriptions.Item label="Filters">{filterSummary()}</Descriptions.Item>
            {session.source_type === 'session' && (
              <>
                <Descriptions.Item label="Source Task">
                  {session.source_session_id ? (
                    <a onClick={() => navigate(`/moderation/${session.source_session_id}`)}>
                      {session.source_session_name || `#${session.source_session_id}`}
                    </a>
                  ) : 'Source removed'}
                </Descriptions.Item>
                <Descriptions.Item label="Last Synced">
                  {session.last_synced_at ? new Date(session.last_synced_at).toLocaleString() : '-'}
                </Descriptions.Item>
              </>
            )}
            <Descriptions.Item label="Created">{new Date(session.created_at).toLocaleString()}</Descriptions.Item>
            <Descriptions.Item label="Started">
              {session.started_at ? new Date(session.started_at).toLocaleString() : '-'}
            </Descriptions.Item>
            {session.completed_at && (
              <Descriptions.Item label="Completed" span={2}>
                {new Date(session.completed_at).toLocaleString()}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        {/* Decisions History */}
        {decisions.length > 0 && (
          <Card title={`Decisions (${decisions.length})`}>
            <Table
              columns={decisionColumns}
              dataSource={decisions}
              rowKey="id"
              pagination={{ pageSize: 20 }}
              size="small"
            />
          </Card>
        )}
      </Card>

      {/* Moderation Viewer Modal */}
      {viewerOpen && session.status === 'active' && (
        <ModerationViewer
          sessionId={session.id}
          sessionName={session.name}
          datasetType={session.dataset_type || 'Photo'}
          currentIndex={session.current_index}
          totalImages={session.total_images}
          elapsedSeconds={session.elapsed_seconds}
          lastResumedAt={session.last_resumed_at}
          isLive={isLive}
          onClose={handleViewerClose}
        />
      )}

      <Modal
        title="Convert to Collection"
        open={convertOpen}
        onCancel={() => setConvertOpen(false)}
        onOk={handleConvert}
        okText="Convert"
        confirmLoading={converting}
        okButtonProps={{ icon: <FolderAddOutlined /> }}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Copies the selected images (URL, caption &amp; indexed params) into a Collection
          dataset for later use. Files are not copied, and duplicates are skipped.
        </Text>

        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Which images</Text>
          <Radio.Group
            value={convertSource}
            onChange={e => setConvertSource(e.target.value)}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="approved">Approved ({session.approved_count})</Radio.Button>
            <Radio.Button value="declined">Declined ({session.declined_count})</Radio.Button>
            <Radio.Button value="favorites">Favorites</Radio.Button>
          </Radio.Group>
        </div>

        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Destination</Text>
          <Select
            style={{ width: '100%', marginBottom: 8 }}
            value={convertTarget}
            onChange={setConvertTarget}
            options={[
              { value: 'new', label: '➕ New collection…' },
              ...collections.map(c => ({ value: c.id, label: `${c.name} (${c.image_count})` })),
            ]}
          />
          {convertTarget === 'new' && (
            <Input
              placeholder="New collection name"
              value={convertName}
              onChange={e => setConvertName(e.target.value)}
              onPressEnter={handleConvert}
            />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default ModerationSessionView;
